import {
  getBrandingTitleForDocumentType,
  INVOICE_DOCUMENT_TYPES,
} from "./invoiceDocumentTypes";
import { formatInvoiceClientDisplayAddress } from "./applicationCostHelpers";
import {
  getInvoiceSettingTradingName,
  getInvoiceSettingLegalName,
  getInvoiceSettingAddressLines,
  getInvoiceSettingEmail,
} from "./invoiceSettingHelpers";

export { getBrandingTitleForDocumentType };

export function getTaxTypeDisplayLabel(taxType) {
  const value = String(taxType || "").toLowerCase();
  if (value === "inclusive") return "Tax inclusive";
  if (value === "exclusive") return "Tax exclusive";
  if (value === "mixed") return "Mixed";
  return "No tax";
}

export function getBrandingThemeLabel(branding) {
  return String(branding?.name || "").trim();
}

export function resolveInvoiceDisplayTitle({ branding, documentType }) {
  const title = getBrandingTitleForDocumentType(branding, documentType);
  if (title) return title;

  const typeDef = INVOICE_DOCUMENT_TYPES.find(
    (item) => item.value === documentType,
  );
  return typeDef?.label?.toUpperCase() || "INVOICE";
}

export function getBranchDisplayName(branch) {
  if (!branch) return "";
  return String(branch.agencyname || branch.name || "").trim();
}

/** Human-readable branch for invoice email signatures (not raw branch ID lists). */
export function resolveInvoiceEmailBranchLabel(user = {}) {
  if (!user || typeof user !== "object") return "";

  const pick = (value) => {
    if (value === null || value === undefined) return "";
    if (Array.isArray(value)) return "";
    const text = String(value).trim();
    if (!text) return "";
    if (/^\d+(\s*,\s*\d+)+$/.test(text)) return "";
    if (text.includes(",") && /^[\d,\s]+$/.test(text)) return "";
    if (/^\d+$/.test(text)) return "";
    return text;
  };

  return (
    pick(user.branch_name) ||
    pick(user.agencyname) ||
    pick(user.office_name) ||
    pick(user.main_branch_name) ||
    ""
  );
}

export function getBranchAgencyName(branch) {
  if (!branch) return "";
  return String(branch.agencyname || "").trim();
}

export function getBranchAgentName(branch) {
  if (!branch) return "";
  return String(branch.agentname || branch.agentName || "").trim();
}

export function getBranchManagerName(branch) {
  return getBranchAgentName(branch);
}

export function buildBranchFromExtraLines(branch, { includeBranchName = true } = {}) {
  if (!branch) return [];

  const lines = [];
  const branchName = getBranchAgencyName(branch);
  const managerName = getBranchAgentName(branch);

  if (includeBranchName && branchName) {
    lines.push(`Branch: ${branchName}`);
  }
  if (managerName) {
    lines.push(`Manager: ${managerName}`);
  }

  return lines;
}

export function getBranchEmail(branch) {
  if (!branch) return "";
  return String(branch.email || "").trim();
}

export function formatBranchContactNumber(branch) {
  if (!branch) return "";

  if (branch.country_code && branch.contact) {
    const code = String(branch.country_code).trim();
    const contact = String(branch.contact).trim();
    return `${code} ${contact}`.trim();
  }

  const contact = branch.contact || branch.phone || branch.mobile || "";
  return String(contact).trim();
}

export function getBranchAddress(branch) {
  if (!branch) return "";
  return String(branch.address || "").trim();
}

export function getBranchCountry(branch) {
  if (!branch) return "";
  return String(branch.country || "").trim();
}

function isPartyDetailLabelLine(line) {
  return /^[^:]+:\s*$/.test(String(line || "").trim());
}

function isPartyDetailInlineField(line) {
  return /^[^:]+:\s+\S/.test(String(line || "").trim());
}

/**
 * Parse branding contact_details into company name + labeled fields.
 * Merges:
 *   Address:
 *   123 Street,
 *   Sydney NSW
 * into:
 *   { label: "Address", value: "123 Street, Sydney NSW" }
 */
export function parseBrandingCompanyDetails(text) {
  const rawLines = String(text || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (!rawLines.length) {
    return { name: "", lines: [], fields: [] };
  }

  const name = rawLines[0];
  const detailLines = rawLines.slice(1);
  const fields = [];
  let index = 0;

  while (index < detailLines.length) {
    const line = detailLines[index];
    const inlineMatch = line.match(/^([^:]+):\s+(.+)$/);
    const labelOnlyMatch = line.match(/^([^:]+):\s*$/);

    if (inlineMatch) {
      fields.push({
        label: inlineMatch[1].trim(),
        value: inlineMatch[2].trim(),
      });
      index += 1;
      continue;
    }

    if (labelOnlyMatch) {
      const label = labelOnlyMatch[1].trim();
      index += 1;
      const valueParts = [];

      while (index < detailLines.length) {
        const next = detailLines[index];
        if (isPartyDetailLabelLine(next) || isPartyDetailInlineField(next)) {
          break;
        }
        valueParts.push(next);
        index += 1;
      }

      fields.push({
        label,
        value: valueParts.join(" ").replace(/\s+/g, " ").trim(),
      });
      continue;
    }

    fields.push({ label: "", value: line });
    index += 1;
  }

  const lines = fields
    .filter((field) => field.value)
    .map((field) =>
      field.label ? `${field.label}: ${field.value}` : field.value,
    );

  return { name, lines, fields };
}

function normalizePartyFieldLabel(label) {
  const raw = String(label || "").trim().toLowerCase();
  if (!raw) return "";
  if (raw === "mobile" || raw === "tel" || raw === "telephone") return "phone";
  if (raw === "e-mail") return "email";
  if (raw.startsWith("web")) return "website";
  return raw;
}

/** Collapse duplicate Phone/Email/Address lines from branding + branch merge. */
export function dedupePartyFieldsByLabel(fields = []) {
  const list = Array.isArray(fields) ? fields : [];
  const seen = new Set();
  const result = [];

  list.forEach((field) => {
    const value = String(field?.value || "").trim();
    if (!value) return;

    const bucket = normalizePartyFieldLabel(field?.label);
    if (bucket) {
      if (seen.has(bucket)) return;
      seen.add(bucket);
    }

    result.push({
      label: field?.label ?? "",
      value,
    });
  });

  return result;
}

const FROM_TOGGLE_LABELS = {
  showPhone: ["phone", "mobile", "tel", "telephone"],
  showEmail: ["email", "e-mail"],
  showWebsite: ["website", "web"],
  showAddress: ["address"],
};

export function filterPartyFieldsByFromOptions(fields = [], fromOptions = {}) {
  const opts = fromOptions && typeof fromOptions === "object" ? fromOptions : {};
  const list = Array.isArray(fields) ? fields : [];

  return list.filter((field) => {
    const labelNorm = normalizePartyFieldLabel(field?.label);
    if (!labelNorm) return true;

    for (const [toggleKey, aliases] of Object.entries(FROM_TOGGLE_LABELS)) {
      if (aliases.includes(labelNorm) && opts[toggleKey] === false) {
        return false;
      }
    }
    return true;
  });
}

export function prepareFromPartyFields(fields = [], fromOptions = {}) {
  return filterPartyFieldsByFromOptions(
    dedupePartyFieldsByLabel(fields),
    fromOptions,
  );
}

function normalizePaymentAdviceLabel(label) {
  return String(label || "")
    .trim()
    .toLowerCase()
    .replace(/:$/, "");
}

export function resolvePaymentAdviceFieldToggleKey(label) {
  const n = normalizePaymentAdviceLabel(label);
  if (!n) return null;
  if (n.includes("payment term")) return "showPaymentTerms";
  if (n === "account name") return "showAdviceAccountName";
  if (n === "bank" || n.startsWith("bank name")) return "showAdviceBank";
  if (n === "bsb") return "showAdviceBsb";
  if (n.includes("account number") || n === "account no" || n === "acc") {
    return "showAdviceAccountNumber";
  }
  if (n === "reference" || n.includes("payment reference")) {
    return "showAdviceReference";
  }
  if (
    n.includes("payment quer") ||
    n.includes("for any payment") ||
    n.includes("contact")
  ) {
    return "showAdviceContact";
  }
  return null;
}

export function filterPaymentAdviceBlocks(blocks = [], footerOptions = {}) {
  const list = Array.isArray(blocks) ? blocks : [];
  const opts =
    footerOptions && typeof footerOptions === "object" ? footerOptions : {};

  return list.filter((block) => {
    if (!block || block.type !== "field") return true;
    const key = resolvePaymentAdviceFieldToggleKey(block.label);
    if (!key) return true;
    return opts[key] !== false;
  });
}

const PAYMENT_BANK_LABELS = [
  "account name",
  "bank",
  "bsb",
  "account number",
  "reference",
  "swift",
  "iban",
  "sort code",
  "routing number",
];

function isPaymentBankLabel(label) {
  const normalized = String(label || "").trim().toLowerCase();
  return PAYMENT_BANK_LABELS.some(
    (item) => normalized === item || normalized.startsWith(`${item} `),
  );
}

function isLabelLine(line) {
  return /^[^:]+:\s*.*$/.test(String(line || "").trim());
}

export function parsePaymentAdviceContent(text) {
  const lines = String(text || "").split(/\r?\n/);
  const blocks = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index].trim();
    index += 1;
    if (!line) continue;

    const inlineMatch = line.match(/^([^:]+):\s*(.+)$/);
    if (inlineMatch) {
      blocks.push({
        type: "field",
        label: inlineMatch[1].trim(),
        value: inlineMatch[2].trim(),
      });
      continue;
    }

    const labelOnlyMatch = line.match(/^([^:]+):\s*$/);
    if (labelOnlyMatch) {
      const label = labelOnlyMatch[1].trim();
      let peek = index;
      while (peek < lines.length && !lines[peek].trim()) peek += 1;
      const nextLine = peek < lines.length ? lines[peek].trim() : "";

      if (isPaymentBankLabel(label) || (nextLine && !isLabelLine(nextLine))) {
        const valueLines = [];
        while (index < lines.length) {
          const current = lines[index].trim();
          if (!current) {
            index += 1;
            break;
          }
          if (isLabelLine(current)) break;
          valueLines.push(current);
          index += 1;
        }
        blocks.push({
          type: "field",
          label,
          value: valueLines.join(" "),
        });
      } else {
        blocks.push({ type: "heading", text: `${label}:` });
      }
      continue;
    }

    blocks.push({ type: "paragraph", text: line });
  }

  return blocks;
}

export function parseTermsConditionsContent(text) {
  const rawLines = String(text || "").split(/\r?\n/).map((line) => line.trim());
  const lines = [];
  let index = 0;

  while (index < rawLines.length) {
    const line = rawLines[index];
    index += 1;
    if (!line) continue;

    const inlineNumbered = line.match(/^(\d+)[.)]\s*(.+)$/);
    if (inlineNumbered) {
      lines.push(`${inlineNumbered[1]}. ${inlineNumbered[2].trim()}`);
      continue;
    }

    if (/^\d+$/.test(line)) {
      let peek = index;
      while (peek < rawLines.length && !rawLines[peek]) peek += 1;
      if (peek < rawLines.length) {
        lines.push(`${line}. ${rawLines[peek]}`);
        index = peek + 1;
        continue;
      }
    }

    lines.push(line);
  }

  return lines;
}

export function buildClassicInvoiceBillToLines(clientProfile = {}) {
  const lines = [];
  const isAgencyBillTo = Boolean(clientProfile?.isAgencyBillTo);
  const name = String(clientProfile?.fullName || "").trim();
  if (name) lines.push(name);

  const email = String(clientProfile?.email || "").trim();
  if (isAgencyBillTo || email) {
    lines.push(`Email: ${email || "—"}`);
  }

  const phone = String(clientProfile?.phone || "").trim();
  if (isAgencyBillTo) {
    lines.push(`Phone: ${phone || "phone missing"}`);
  } else if (phone) {
    lines.push(`Phone: ${phone}`);
  }

  if (!isAgencyBillTo) {
    const address = formatInvoiceClientDisplayAddress(clientProfile);
    if (address) lines.push(address);
  }

  return lines;
}

export function buildClassicInvoiceCompanyContact({
  invoiceSetting = null,
  branch = null,
  brandingFrom = null,
  companyName = "",
} = {}) {
  const name =
    String(brandingFrom?.name || "").trim() ||
    getInvoiceSettingLegalName(invoiceSetting) ||
    getInvoiceSettingTradingName(invoiceSetting) ||
    String(companyName || "").trim();

  const fieldRows = [];

  if (brandingFrom?.fields?.length) {
    brandingFrom.fields.forEach((field) => {
      const value = String(field?.value || "").trim();
      if (!value) return;
      fieldRows.push({
        label: String(field?.label || "").trim(),
        value,
      });
    });
  } else {
    const addressLines = getInvoiceSettingAddressLines(invoiceSetting);
    let address = addressLines.join(", ");
    if (!address) {
      const branchAddress = getBranchAddress(branch);
      const branchCountry = getBranchCountry(branch);
      address = [branchAddress, branchCountry].filter(Boolean).join(", ");
    }

    const phone =
      String(invoiceSetting?.c_phone || "").trim() ||
      formatBranchContactNumber(branch);
    const mobile = String(invoiceSetting?.c_mobile || "").trim();
    const email = getInvoiceSettingEmail(invoiceSetting) || getBranchEmail(branch);
    const website = String(invoiceSetting?.website || "").trim();

    if (address) fieldRows.push({ label: "Address", value: address });
    if (phone) fieldRows.push({ label: "Phone", value: phone });
    if (mobile) fieldRows.push({ label: "Mobile", value: mobile });
    if (email) fieldRows.push({ label: "Email", value: email });
    if (website) fieldRows.push({ label: "Website", value: website });
  }

  return { name, fieldRows };
}

/** @deprecated Use buildClassicInvoiceCompanyContact instead. */
export function buildClassicInvoiceCompanyContactLines(options = {}) {
  const { name, fieldRows } = buildClassicInvoiceCompanyContact(options);
  const lines = [];
  if (name) lines.push(name);
  fieldRows.forEach((row) => {
    lines.push(row.label ? `${row.label}: ${row.value}` : row.value);
  });
  return lines;
}

function roundPreviewMoney(value) {
  return Math.round((Number(value) || 0) * 100) / 100;
}

/** Xero-style amount due: TOTAL − paid when payments exist. */
export function resolveInvoiceDocumentAmountDue({
  displayTotal = 0,
  paidTotal = 0,
  explicitAmountDue = null,
} = {}) {
  const total = Math.max(Number(displayTotal) || 0, 0);
  const paid = Math.max(Number(paidTotal) || 0, 0);

  if (paid > 0) {
    return roundPreviewMoney(Math.max(0, total - paid));
  }

  if (
    explicitAmountDue !== null &&
    explicitAmountDue !== undefined &&
    explicitAmountDue !== ""
  ) {
    return Math.max(0, Number(explicitAmountDue) || 0);
  }

  return roundPreviewMoney(total);
}

export function resolveInvoicePreviewTotals({
  gross_subtotal = 0,
  subtotal = 0,
  discount_total = 0,
  total_amount = 0,
  paid_amount = 0,
  amount_due,
  lineItems = [],
} = {}) {
  const grossSubtotal = Math.max(0, Number(gross_subtotal) || 0);
  const lineNetSubtotal = Math.max(0, Number(subtotal) || 0);
  const lineDiscountTotal = (lineItems || []).reduce(
    (sum, row) => sum + Math.abs(Number(row.discount) || 0),
    0,
  );
  const invoiceDiscount = Math.max(0, Number(discount_total) || 0);
  const displayDiscount =
    invoiceDiscount > 0 ? invoiceDiscount : lineDiscountTotal;

  const displaySubtotal =
    grossSubtotal > 0
      ? grossSubtotal
      : roundPreviewMoney(lineNetSubtotal + displayDiscount);

  const discountAlreadyInLineAmounts =
    displayDiscount > 0 &&
    grossSubtotal > 0 &&
    Math.abs(grossSubtotal - displayDiscount - lineNetSubtotal) < 0.02;

  let displayTotal = 0;
  if (discountAlreadyInLineAmounts) {
    displayTotal = lineNetSubtotal;
  } else if (displayDiscount > 0) {
    displayTotal = roundPreviewMoney(
      Math.max(0, displaySubtotal - displayDiscount),
    );
  } else {
    displayTotal =
      lineNetSubtotal || Math.max(0, Number(total_amount) || 0);
  }

  const paidTotal = Math.max(0, Number(paid_amount) || 0);
  const displayAmountDue = resolveInvoiceDocumentAmountDue({
    displayTotal,
    paidTotal,
    explicitAmountDue: amount_due,
  });

  return {
    displaySubtotal,
    displayDiscount,
    hasDiscount: displayDiscount > 0,
    displayTotal,
    displayAmountDue,
    paidTotal,
  };
}

export function formatInvoicePreviewMetaDate(value) {
  if (!value) return "—";

  const normalized = String(value).trim();
  if (!normalized) return "—";

  const date = new Date(
    normalized.includes("T") ? normalized : `${normalized}T00:00:00`,
  );

  if (Number.isNaN(date.getTime())) return normalized;

  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function buildInvoiceLessPaidLabelParts(paymentDate) {
  const formatted = formatInvoicePreviewMetaDate(paymentDate);
  if (!formatted || formatted === "—") {
    return { prefix: "Less:", detail: "Amount Paid" };
  }
  return { prefix: "Less:", detail: `Payment (${formatted})` };
}
